import React, { useState, useEffect } from 'react';
import { useData } from '../../contexts/DataContext';
import { Plus, Trash2, Eye, EyeOff, Copy, Check, Server, Ban, ShieldCheck, Save } from 'lucide-react'; 
import { Client as ClientType } from '../../types';
import { useNavigate } from 'react-router-dom';

export const ClientManagement: React.FC = () => {
  const { clients = [], servers = [], addClient, deleteClient, updateClientStatus, saveClientTelegramId } = useData();
  const navigate = useNavigate();
  
  const [showModal, setShowModal] = useState(false);
  const [showPasswords, setShowPasswords] = useState<Record<string, boolean>>({});
  const [copiedStates, setCopiedStates] = useState<Record<string, boolean>>({});
  const [newClient, setNewClient] = useState({ name: '', username: '', password: '', email: '' });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [telegramIds, setTelegramIds] = useState<Record<string, string>>({});

  useEffect(() => {
    const initialIds: Record<string, string> = {};
    if (clients) {
      clients.forEach(client => {
        if (client.telegram_chat_id) {
          initialIds[client.id] = client.telegram_chat_id;
        }
      });
      setTelegramIds(initialIds);
    }
  }, [clients]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    try {
      await addClient(newClient);
      setNewClient({ name: '', username: '', password: '', email: '' });
      setShowModal(false);
    } catch (error) {
      alert('Error creating client. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDeleteClient = (clientId: string, clientName: string) => {
    if (confirm(`Are you sure you want to delete the client "${clientName}"? This action cannot be undone.`)) {
      deleteClient(clientId);
    }
  };

  const handleStatusChange = (client: ClientType) => {
    const newStatus = client.status === 'active' ? 'banned' : 'active';
    const actionText = newStatus === 'banned' ? 'suspend' : 'reactivate';
    if (confirm(`Are you sure you want to ${actionText} the client "${client.name}"?`)) {
      updateClientStatus(client.id, newStatus);
    }
  };
  
  const handleSaveChatId = (clientId: string) => {
    const chatId = telegramIds[clientId] || '';
    if (!chatId.trim()) {
      alert('Please enter a Chat ID.');
      return;
    }
    saveClientTelegramId(clientId, chatId.trim())
      .then(() => alert('Chat ID saved successfully.'))
      .catch((err) => alert(`Error saving Chat ID: ${err.message}`));
  };

  const togglePasswordVisibility = (clientId: string) => setShowPasswords(prev => ({ ...prev, [clientId]: !prev[clientId] }));

  const copyToClipboard = async (text: string, key: string) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopiedStates(prev => ({ ...prev, [key]: true }));
      setTimeout(() => {
        setCopiedStates(prev => ({ ...prev, [key]: false }));
      }, 2000);
    } catch (err) {
      console.error('Error copying:', err);
    }
  };
  
  const getClientUrl = (hash: string) => `https://lisa.athosfera.com/client/${hash}`;
  
  const getFullCredentials = (client: ClientType) => {
    return `🔐 Access Credentials - Lisa Server

👤 Client: ${client.name}
🌐 Access URL: ${getClientUrl(client.hash)}
👤 Username: ${client.username}
🔑 Password: ${client.password}

📋 Instructions:
1. Access the provided URL
2. Use the credentials to log in
3. From your panel, you can manage your assigned servers

Welcome to Lisa Server! 🚀`;
  };

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h2 className="text-2xl font-bold text-gray-900">Client Management</h2>
        <button onClick={() => setShowModal(true)} className="bg-gradient-to-r from-blue-500 to-green-500 text-white px-4 py-2 rounded-lg hover:from-blue-600 hover:to-green-600 transition-all duration-200 flex items-center space-x-2">
          <Plus className="w-4 h-4" />
          <span>New Client</span>
        </button>
      </div>

      <div className="bg-white rounded-xl shadow-lg border overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full">
            <thead className="bg-slate-100 border-b-2 border-slate-200">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-semibold text-slate-600 uppercase tracking-wider">Client</th>
                <th className="px-6 py-3 text-left text-xs font-semibold text-slate-600 uppercase tracking-wider">Status</th>
                <th className="px-6 py-3 text-left text-xs font-semibold text-slate-600 uppercase tracking-wider">Telegram Chat ID</th>
                <th className="px-6 py-3 text-left text-xs font-semibold text-slate-600 uppercase tracking-wider">Credentials</th>
                <th className="px-6 py-3 text-left text-xs font-semibold text-slate-600 uppercase tracking-wider">Servers</th>
                <th className="px-6 py-3 text-left text-xs font-semibold text-slate-600 uppercase tracking-wider">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {clients.map((client) => (
                <tr key={client.id} className={`odd:bg-white even:bg-slate-50 ${client.status === 'banned' ? 'opacity-60 bg-red-50' : ''}`}>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div>
                      <div className="text-sm font-medium text-gray-900">{client.name}</div>
                      <div className="text-sm text-gray-500">{client.email}</div>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${client.status === 'active' ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}`}>
                      {client.status === 'active' ? 'Active' : 'Suspended'}
                    </span>
                  </td>
                   <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex items-center space-x-2">
                      <input 
                        type="text" 
                        placeholder="Paste Chat ID..."
                        className="w-full px-2 py-1 border border-gray-300 rounded-md text-sm"
                        value={telegramIds[client.id] || ''}
                        onChange={(e) => setTelegramIds(prev => ({ ...prev, [client.id]: e.target.value }))}
                      />
                      <button 
                        onClick={() => handleSaveChatId(client.id)}
                        className="p-2 text-blue-600 hover:bg-blue-50 rounded-lg"
                        title="Save Chat ID"
                      >
                        <Save className="w-4 h-4" />
                      </button>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm">
                      <div className="font-medium">Username: {client.username}</div>
                      <div className="flex items-center space-x-2">
                        <span>Password: </span>
                        {showPasswords[client.id] ? <span className="font-mono">{client.password}</span> : <span className="font-mono">••••••••</span>}
                        <button onClick={() => togglePasswordVisibility(client.id)} className="text-gray-400 hover:text-gray-600">
                          {showPasswords[client.id] ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                        </button>
                      </div>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <button
                      onClick={() => navigate(`/admin/servers?clientId=${client.id}`)}
                      className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800 hover:bg-blue-200 transition-colors"
                      title="View this client's servers"
                    >
                      {servers.filter(s => s.assignedTo?.includes(client.id)).length} server(s)
                    </button>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                    <div className="flex items-center justify-end space-x-2">
                       <button
                        onClick={() => handleStatusChange(client)}
                        className={`p-2 rounded-lg ${client.status === 'active' ? 'text-orange-600 hover:text-orange-900 hover:bg-orange-50' : 'text-green-600 hover:text-green-900 hover:bg-green-50'}`}
                        title={client.status === 'active' ? 'Suspend client' : 'Reactivate client'}
                      >
                        {client.status === 'active' ? <Ban className="w-4 h-4" /> : <ShieldCheck className="w-4 h-4" />}
                      </button>
                      <button
                        onClick={() => copyToClipboard(getFullCredentials(client), `full-${client.id}`)}
                        className="text-green-600 hover:text-green-800 hover:bg-green-50 p-2 rounded-lg flex items-center space-x-1"
                        title="Copy full credentials"
                      >
                        {copiedStates[`full-${client.id}`] ? <><Check className="w-4 h-4" /><span>Copied!</span></> : <><Copy className="w-4 h-4" /><span>Copy All</span></>}
                      </button>
                      <button
                        onClick={() => handleDeleteClient(client.id, client.name)}
                        className="text-red-600 hover:text-red-900 hover:bg-red-50 p-2 rounded-lg"
                        title="Delete client"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        {clients.length === 0 && (
          <div className="text-center py-8"><p className="text-gray-500">No registered clients</p></div>
        )}
      </div>

      {showModal && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-96 shadow-lg rounded-md bg-white">
            <h3 className="text-lg font-bold text-gray-900 mb-4">New Client</h3>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Name</label>
                <input type="text" value={newClient.name} onChange={(e) => setNewClient({ ...newClient, name: e.target.value })} className="w-full px-3 py-2 border border-gray-300 rounded-md" required />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Username</label>
                <input type="text" value={newClient.username} onChange={(e) => setNewClient({ ...newClient, username: e.target.value })} className="w-full px-3 py-2 border border-gray-300 rounded-md" required />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Password</label>
                <input type="text" value={newClient.password} onChange={(e) => setNewClient({ ...newClient, password: e.target.value })} className="w-full px-3 py-2 border border-gray-300 rounded-md" required />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Email</label>
                <input type="email" value={newClient.email} onChange={(e) => setNewClient({ ...newClient, email: e.target.value })} className="w-full px-3 py-2 border border-gray-300 rounded-md" required />
              </div>
              <div className="flex justify-end space-x-3 pt-4">
                <button type="button" onClick={() => setShowModal(false)} className="px-4 py-2 border rounded-md">Cancel</button>
                <button type="submit" disabled={isSubmitting} className="px-4 py-2 bg-gradient-to-r from-blue-500 to-green-500 text-white rounded-md disabled:opacity-50">{isSubmitting ? 'Creating...' : 'Create Client'}</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};